/**
 * Share panel — mint, list, and revoke the named links that grant access.
 *
 * Two things drive the design. A minted link is shown **once** (the server
 * keeps only its hash), so the moment it appears has to be unmissable and
 * copyable. And the label on a key is the identity every edit through it is
 * attributed to, so the form asks for a person's name rather than treating one
 * shared secret as good enough.
 */
import { esc } from "@trip/shared/engine/time";
import { store } from "../store.js";
import { banner } from "./banner.js";

interface TripKey {
  id: string;
  role: "edit" | "read";
  label: string;
  created_at: string;
  created_by: string | null;
  last_used_at: string | null;
}

const api = () => `/api/trip/${encodeURIComponent(store.tripId)}`;

/** Access travels in the header, exactly as sync.ts and changelog.ts send it. */
const auth = (extra: Record<string, string> = {}) => ({
  ...extra,
  ...(store.key ? { "x-trip-key": store.key } : {}),
});

/**
 * Only holders who may actually manage keys get the button. Editing is not
 * sufficient — the server also requires trip ownership — so ask it rather
 * than guessing, and let a 403 hide the button.
 */
export async function canShare(): Promise<boolean> {
  if (store.role !== "edit") return false;
  try {
    return (await fetch(`${api()}/keys`, { headers: auth() })).ok;
  } catch {
    return false;
  }
}

function whenText(iso: string | null): string {
  if (!iso) return "never opened";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days === 0) return "opened today";
  if (days === 1) return "opened yesterday";
  if (days < 30) return `opened ${days} days ago`;
  return `opened ${new Date(iso).toLocaleDateString()}`;
}

function rowHTML(k: TripKey): string {
  return `
    <div class="share-row" data-key-id="${esc(k.id)}">
      <div class="share-who">
        <span class="share-label">${esc(k.label)}</span>
        <span class="badge ${k.role === "edit" ? "b-edit" : "b-view"}">${k.role === "edit" ? "can edit" : "view only"}</span>
      </div>
      <div class="share-meta">${esc(whenText(k.last_used_at))}</div>
      <button class="btn-det share-revoke" data-action="share-revoke" data-id="${esc(k.id)}" data-label="${esc(k.label)}">Revoke</button>
    </div>`;
}

async function refreshList() {
  const list = document.getElementById("share-list");
  if (!list) return;
  try {
    const res = await fetch(`${api()}/keys`, { headers: auth() });
    if (!res.ok) throw new Error(String(res.status));
    const { keys } = (await res.json()) as { keys: TripKey[] };
    list.innerHTML = keys.length
      ? keys.map(rowHTML).join("")
      : `<div class="share-empty">No links yet.</div>`;
  } catch {
    list.innerHTML = `<div class="share-empty">Couldn't load the links — check your connection.</div>`;
  }
}

export function openShare() {
  const modalEl = document.getElementById("modal")!;
  modalEl.style.maxWidth = "560px";
  modalEl.innerHTML = `
    <div class="m-head">
      <div class="m-cat">Sharing</div><h3>Invite links</h3>
      <button class="m-close" data-action="close" aria-label="close">✕</button>
    </div>
    <div class="m-body">
      <p class="share-intro">
        Each link carries a name — whatever that person changes shows up under it in the change log.
        Give people their own so you can revoke one without breaking the rest.
        Links are private URLs with nothing readable in them. View-only links work instantly;
        <b>editing requires signing in</b> — an edit link is view-only until they do.
      </p>

      <div id="share-list" class="share-list"><div class="share-empty">Loading…</div></div>

      <form id="share-form" class="share-form" novalidate>
        <div class="share-fields">
          <label class="share-field">
            <span>Their name</span>
            <input id="share-label" type="text" maxlength="60" placeholder="e.g. Mum" required>
          </label>
          <label class="share-field share-field-role">
            <span>Access</span>
            <select id="share-role">
              <option value="read" selected>View only</option>
              <option value="edit">Can edit (sign-in required)</option>
            </select>
          </label>
        </div>
        <label class="share-field">
          <span>Email it to them <span class="share-optional">— optional, you can copy the link instead</span></span>
          <input id="share-email" type="email" placeholder="them@example.com" autocomplete="off">
        </label>
        <button class="btn-cta" type="submit" id="share-submit">Create link</button>
      </form>

      <div id="share-result" class="share-result" style="display:none"></div>
    </div>`;
  document.getElementById("modalbg")!.classList.add("open");
  void refreshList();

  document.getElementById("share-form")!.addEventListener("submit", async (e) => {
    e.preventDefault();
    const label = (document.getElementById("share-label") as HTMLInputElement).value.trim();
    const role = (document.getElementById("share-role") as HTMLSelectElement).value;
    const email = (document.getElementById("share-email") as HTMLInputElement).value.trim();
    const submit = document.getElementById("share-submit") as HTMLButtonElement;
    const result = document.getElementById("share-result")!;
    if (!label) {
      result.style.display = "";
      result.className = "share-result share-error";
      result.textContent = "Add a name so their changes are attributable.";
      return;
    }

    submit.disabled = true;
    try {
      const res = await fetch(`${api()}/invite`, {
        method: "POST",
        headers: auth({ "content-type": "application/json" }),
        body: JSON.stringify({ role, label, email: email || undefined }),
      });
      const data = (await res.json()) as { link?: string; emailed?: boolean; error?: string };
      if (!res.ok || !data.link) {
        result.style.display = "";
        result.className = "share-result share-error";
        result.textContent = data.error || "Couldn't create that link.";
        return;
      }
      // Show it on the origin they are actually using. The server builds the
      // emailed link from APP_BASE_URL — right in production, but the API port
      // in dev, and a stale setting should not produce a link that 404s.
      const parsed = new URL(data.link, location.origin);
      const shown = `${location.origin}${parsed.pathname}${parsed.hash}`;

      result.style.display = "";
      result.className = "share-result share-ok";
      result.innerHTML = `
        <div class="share-result-head">Link for <b>${esc(label)}</b>${data.emailed ? ` — emailed to ${esc(email)}` : ""}</div>
        <input class="share-link" id="share-link" readonly value="${esc(shown)}">
        <div class="share-copyrow">
          <button type="button" class="btn-cta" data-action="share-copy">Copy link</button>
          <span class="share-warn">Copy it now — it can't be shown again.</span>
        </div>`;
      (document.getElementById("share-form") as HTMLFormElement).reset();
      void refreshList();
    } catch {
      result.style.display = "";
      result.className = "share-result share-error";
      result.textContent = "Couldn't reach the server.";
    } finally {
      submit.disabled = false;
    }
  });
}

export async function copyShareLink() {
  const input = document.getElementById("share-link") as HTMLInputElement | null;
  if (!input) return;
  try {
    await navigator.clipboard.writeText(input.value);
    banner("Link copied.", "ok");
  } catch {
    // clipboard is blocked in some contexts — selecting it still lets them copy
    input.select();
    banner("Press Ctrl+C to copy the selected link.", "");
  }
}

/**
 * Revoking is irreversible and instantly locks someone out, so it asks first —
 * and names who, because "Revoke" on the wrong row is an easy mistake.
 */
export async function revokeShareKey(id: string, label: string) {
  if (!confirm(`Revoke the link for ${label}?\n\nIt stops working immediately. Anyone using it loses access, and the link cannot be restored.`)) {
    return;
  }
  try {
    const res = await fetch(`${api()}/keys/${encodeURIComponent(id)}`, { method: "DELETE", headers: auth() });
    if (!res.ok) {
      banner("Couldn't revoke that link.", "warn");
      return;
    }
    banner(`Revoked ${label}'s link.`, "ok");
    void refreshList();
  } catch {
    banner("Couldn't reach the server.", "warn");
  }
}
